import { useEffect, useRef, useState } from "react";
import PropTypes from 'prop-types';

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "@mui/material/Table";
import DataTable from "../../examples/Tables/DataTable";
import data from "../dashboard/components/Projects/data";
import useApi from "../../components/Utils/api";
import MDInput from "../../components/MDInput";
import toast from "react-hot-toast";
import subDays from "date-fns/subDays";
import startOfMonth from "date-fns/startOfMonth";
import addMonths from "date-fns/addMonths";
import endOfMonth from "date-fns/endOfMonth";
import { useNavigate } from "react-router-dom";
import Icon from "@mui/material/Icon";
import BulkAssignMerchant from "./bulk-assign";
import PermissionControl from "../../components/Utils/PermissionControl";
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import Tooltip from "@mui/material/Tooltip";
import Papa from 'papaparse';

function Merchants() {
  const { merchantTablecolumns } = data();
  const [merchants, setMerchants] = useState([]);
  const [unAssignedMerchants, setUnAssignedMerchants] = useState([]);
  const [merchantGroups, setMerchantGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [serialNumber, setSerialNumber] = useState("");
  const [serialSearchResult, setSerialSearchResult] = useState(null);
  const navigate = useNavigate();
  const api = useApi();
  const getMerchants = (isDeleted = false) => {
    const data = {
      "ownerName": "",
      "mid": "",
      "referenceId": ""
    };
    api.get("/adminPortal/merchant/all", { params: { isDeleted } })
    .then((response) => {
    //SUCCESS
    console.log(response)
    if (response.status === 200 && response.data?.responseCode === 200) {
      const merchants = response.data?.data?.body || [];
      setMerchants(merchants);
      return;
    }
    //BUSINESS ERROR (handled by backend)
    toast.error(
      response.data?.error ||
      response.data?.message ||
      "Failed to fetch merchants"
    );
  })
  .catch((error) => {
    //FORBIDDEN
    if (error.response?.status === 403) {
      toast.error("You do not have permission to perform this action", {
        id: "PermissionError",
      });
      return;
    }
    //UNAUTHORIZED
    if (error.response?.status === 401) {
      toast.error("Session expired. Please login again.");
      return;
    }
    //SERVER ERROR
    if (error.response?.status === 500) {
      toast.error("Internal server error. Please try again later.");
      return;
    }
    //NETWORK / UNKNOWN ERROR
    toast.error("Unable to connect to server. Check your internet connection.");
    console.error("Merchant fetch error:", error);
  });
}

  useEffect(() => {
    getMerchants(activeTab === 1);
    setSerialNumber("");
    setSerialSearchResult(null);
  },[activeTab]);

  const handleSerialSearch = () => {
    const trimmed = serialNumber.trim();
    if (!trimmed) {
      setSerialSearchResult(null);
      return;
    }
    api.get(`/adminPortal/merchant/by-serial/${trimmed}`)
      .then((response) => {
        if (response.status === 200 && response.data?.responseCode === 200 && response.data?.data) {
          setSerialSearchResult([response.data.data]);
          return;
        }
        setSerialSearchResult([]);
        toast.error(response.data?.responseMessage || "No merchant found for this serial number");
      })
      .catch((error) => {
        setSerialSearchResult([]);
        if (error.response?.status === 403) {
          toast.error("You do not have permission to perform this action");
        } else if (error.response?.status === 404) {
          toast.error("No merchant found for this serial number");
        } else {
          toast.error("Failed to search by serial number");
        }
      });
  };

  const handleClearSerialSearch = () => {
    setSerialNumber("");
    setSerialSearchResult(null);
  };

  const rowHandler = (row, pageRow)  => {
    // console.log("triggered", pageRow)
    navigate(`/merchants/${pageRow.original.mid}`, {
      state: pageRow.original
    })
  }

  const handleViewClick = (mid, merchantData) => {
    navigate(`/merchants/${mid}`, {
      state: merchantData
    });
  };

  const handleRestoreMerchant = (merchantData) => {
    if (!window.confirm(`Are you sure you want to restore merchant "${merchantData.name}"?`)) {
      return;
    }

    api.put(`/adminPortal/merchant/restore/${merchantData.id}`)
      .then((response) => {
        if (response.status === 200 && response.data?.responseCode === 200) {
          toast.success("Merchant restored successfully");
          getMerchants(true); // Refresh deleted merchants list
        } else {
          toast.error(response.data?.message || "Failed to restore merchant");
        }
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error("You do not have permission to restore merchants");
        } else if (error.response?.status === 401) {
          toast.error("Session expired. Please login again.");
        } else {
          toast.error("Failed to restore merchant. Please try again.");
        }
        console.error("Restore merchant error:", error);
      });
  };

  const handleDeleteMerchant = (merchantData) => {
    if (!window.confirm(`Are you sure you want to delete merchant "${merchantData.name}"? This action can be undone from the Deleted Merchants tab.`)) {
      return;
    }

    api.delete(`/adminPortal/merchant/delete/${merchantData.id}`)
      .then((response) => {
        if (response.status === 200 && response.data?.responseCode === 200) {
          toast.success("Merchant deleted successfully");
          getMerchants(false); // Refresh active merchants list
        } else {
          toast.error(response.data?.message || "Failed to delete merchant");
        }
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error("You do not have permission to delete merchants");
        } else if (error.response?.status === 401) {
          toast.error("Session expired. Please login again.");
        } else {
          toast.error("Failed to delete merchant. Please try again.");
        }
        console.error("Delete merchant error:", error);
      });
  };

  const generateCSV = () => {
      if (merchants.length === 0) {
        return '';
      }
  
      // Define all headers from the payload
      const headers = [
        'ID',
        'Business Name',
        'Counter ID',
        'Vendor ID',
        'Beneficiary Name',
        'Beneficiary Account No',
        'Bank Code',
        'Branch Code',
        'Transfer Type',
        'Email',
        'MCC Code',
        'MCC Description',
        'First Name',
        'Last Name',
        'Address',
        'City',
        'State',
        'Pincode',
        'Phone Number',
        'Device IDs',
        'Status',
        'Created Date'
      ];
  
      const summaryWithUppercaseColumns = merchants.map(item => {
        return [
          item.id ?? '',
          item.name ?? '',
          item.counterId ?? '',
          item.vendorId ?? '',
          item.beneficiaryName ?? '',
          item.beneficiaryAccountNo ?? '',
          item.bankCode ?? '',
          item.branchCode ?? '',
          item.transactionCode ?? '',
          item.email1 ?? '',
          item.mccCode ?? '',
          item.mccDescription ?? '',
          item.firstName ?? '',
          item.lastName ?? '',
          item.address ?? '',
          item.city ?? '',
          item.state ?? '',
          item.pincode ?? '',
          item.phoneNumber ?? '',
          Array.isArray(item.deviceIds) ? item.deviceIds.join(', ') : (item.deviceIds ?? ''),
          item.status ?? '',
          item.createdDt ?? ''
        ];
      });
  
      const csvData = [headers, ...summaryWithUppercaseColumns];
  
      const csv = Papa.unparse(csvData, {
        quotes: true,
      });
  
      return csv;
    };
  const handleDownloadCSV = () => {
    if (merchants.length === 0) {
      const toastId = toast.error("No records found to download", { autoClose: false });
      setTimeout(() => toast.dismiss(toastId), 1000);
      return;
    }

    const csvData = generateCSV();

    const currentDate = new Date().toISOString().slice(0, 10);
    const currentTime = new Date().toLocaleTimeString().replace(/:/g, '-');
    const dateTimeString = `${currentDate}_${currentTime}`;

    const fileName = `MerchantReport_${dateTimeString}.csv`;

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const ActionCell = ({ row }) => {
    const isDeleted = activeTab === 1;

    if (isDeleted) {
      return (
        <Tooltip title="Restore Merchant" arrow>
          <div>
            <Icon
              onClick={(e) => {
                e.stopPropagation();
                handleRestoreMerchant(row.original);
              }}
              style={{ cursor: 'pointer', color: '#4caf50' }}
            >
              restore
            </Icon>
          </div>
        </Tooltip>
      );
    }

    return (
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <Tooltip title="View Merchant" arrow>
          <div>
            <RemoveRedEyeIcon
              onClick={(e) => {
                e.stopPropagation();
                handleViewClick(row.original.id, row.original);
              }}
              className='text-inheriti text-xl'
              style={{ cursor: 'pointer' }}
            />
          </div>
        </Tooltip>
        <Tooltip title="Delete Merchant" arrow>
          <div>
            <Icon
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteMerchant(row.original);
              }}
              style={{ cursor: 'pointer', color: '#f44336' }}
            >
              delete
            </Icon>
          </div>
        </Tooltip>
      </div>
    );
  };

  ActionCell.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  };

  const columns = [
    ...merchantTablecolumns,
    {
      Header: activeTab === 1 ? "Actions" : "Details",
      accessor: "details",
      Cell: ActionCell,
      width: "10%",
      align: "left"
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid container justifyContent="flex-end">
              <PermissionControl permission={{ restrict: true, module: "Merchants", subModule: "Create" }}>
                <MDButton variant="gradient" onClick={() => navigate('/merchants/new')} color="info" style={{marginRight: "24px", marginBottom: "12px"}}>
                    Add Merchant
                </MDButton>
              </PermissionControl>
          </Grid>
          <Grid item xs={12} lg={12}>
            <Tabs
              value={activeTab}
              onChange={(event, newValue) => setActiveTab(newValue)}
              sx={{ 
                minHeight: 'auto',
                width: 'fit-content',
                mb: 3,
                backgroundColor: 'transparent',
                '& .MuiTabs-indicator': {
                  height: 3,
                  backgroundColor: '#1976d2',
                  borderRadius: '3px 3px 0 0'
                },
                '& .MuiTabs-flexContainer': {
                  backgroundColor: 'transparent'
                }
              }}
            >
              <Tab 
                label="Active Merchants" 
                sx={{ 
                  minHeight: 'auto', 
                  py: 1.5,
                  px: 3,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  color: '#666',
                  backgroundColor: 'transparent',
                  '&.Mui-selected': {
                    color: '#1976d2',
                    backgroundColor: 'transparent'
                  }
                }} 
              />
              <Tab 
                label="Deleted Merchants" 
                sx={{ 
                  minHeight: 'auto', 
                  py: 1.5,
                  px: 3,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  color: '#666',
                  backgroundColor: 'transparent',
                  '&.Mui-selected': {
                    color: '#1976d2',
                    backgroundColor: 'transparent'
                  }
                }} 
              />
            </Tabs>
          </Grid>
          <Grid item xs={12} lg={12}>
            <MDButton
              variant="gradient"
              onClick={handleDownloadCSV}
              color="info"
              style={{ marginRight: "24px", marginBottom: "12px" }}
            >
              Export
            </MDButton>
            <DataTable
              table={{ columns: columns, rows: serialSearchResult ?? merchants }}
              showTotalEntries={true}
              isSorted={true}
              noEndBorder
              entriesPerPage={true}
              canSearch={true}
              sortBy={[{ id: 'createdDt', desc: true }]}
              extraSearch={
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDInput
                    placeholder="Search by Serial No."
                    value={serialNumber}
                    size="small"
                    onChange={(e) => setSerialNumber(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSerialSearch();
                    }}
                  />
                  <MDButton variant="gradient" color="info" size="small" onClick={handleSerialSearch}>
                    Search
                  </MDButton>
                  {serialSearchResult !== null && (
                    <MDButton variant="outlined" color="secondary" size="small" onClick={handleClearSerialSearch}>
                      Clear
                    </MDButton>
                  )}
                </MDBox>
              }
            />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Merchants;
